const DEFAULT_AXIS_WIDTH = 48;
function cloneBounds(bounds) {
    return { ...bounds };
}
function cloneScaleSlotRuntimeState(state) {
    return {
        ...state,
        domain: state.domain ? [...state.domain] : undefined,
    };
}
export function clonePaneRuntimeSnapshot(snapshot) {
    return {
        paneId: snapshot.paneId,
        bounds: cloneBounds(snapshot.bounds),
        plotBounds: cloneBounds(snapshot.plotBounds),
        axisBounds: Object.fromEntries(Object.entries(snapshot.axisBounds).map(([side, bounds]) => [side, bounds ? cloneBounds(bounds) : bounds])),
        scaleSlotBounds: Object.fromEntries(Object.entries(snapshot.scaleSlotBounds).map(([slotId, bounds]) => [slotId, cloneBounds(bounds)])),
        scaleSlotStates: Object.fromEntries(Object.entries(snapshot.scaleSlotStates).map(([slotId, state]) => [slotId, cloneScaleSlotRuntimeState(state)])),
        headerAnchor: { ...snapshot.headerAnchor },
        labelAnchor: { ...snapshot.labelAnchor },
    };
}
function normalizeVisiblePanes(panes) {
    return panes.filter((pane) => pane.visible).map((pane) => ({
        ...pane,
        heightRatio: pane.heightRatio > 0 ? pane.heightRatio : 1,
    }));
}
function collectVisibleScaleSlots(scaleSlots, side) {
    return scaleSlots.filter((slot) => slot.visible && slot.side === side);
}
function createScaleSlotRuntimeState(slot) {
    return {
        side: slot.side,
        mode: slot.mode,
        visible: slot.visible,
        domain: slot.domain ? [...slot.domain] : undefined,
    };
}
export class PaneLayoutEngine {
    axisWidth;
    constructor(axisWidth = DEFAULT_AXIS_WIDTH) {
        this.axisWidth = axisWidth;
    }
    /**
     * Auto-collapse (user requirement): when no VISIBLE left-axis series can
     * actually render (slot exists but its domain is missing/non-finite —
     * e.g. an overlay whose data never loaded), the left gutter must vanish
     * so the plot reclaims the space, instead of showing ~48px of nothing.
     * Idempotent reconcile — safe on every sync/resize; only transitions
     * mutate (snapshot emit-diffing absorbs the rest, so widget header
     * padding follows automatically). LEFT side only: the right axis always
     * has candle data in practice.
     */
    reconcileEmptyLeftAxis(snapshot, pane, domains) {
        const leftSlots = pane.scaleSlots.filter((slot) => slot.visible && slot.side === "left");
        const hasData = leftSlots.some((slot) => {
            const domain = domains.get(slot.id);
            return !!domain && Number.isFinite(domain[0]) && Number.isFinite(domain[1]);
        });
        const hasGutter = !!snapshot.axisBounds.left;
        if (hasData === hasGutter) {
            return;
        }
        if (!hasData && hasGutter) {
            const width = snapshot.axisBounds.left.width;
            delete snapshot.axisBounds.left;
            snapshot.plotBounds.left = Math.max(0, snapshot.plotBounds.left - width);
            snapshot.plotBounds.width += width;
            snapshot.headerAnchor = { ...snapshot.headerAnchor, left: snapshot.plotBounds.left, width: snapshot.plotBounds.width };
        }
        else if (hasData && !hasGutter) {
            const width = this.axisWidth;
            snapshot.axisBounds.left = {
                top: snapshot.bounds.top,
                left: 0,
                width,
                height: snapshot.bounds.height,
            };
            snapshot.plotBounds.left += width;
            snapshot.plotBounds.width = Math.max(1, snapshot.plotBounds.width - width);
            snapshot.headerAnchor = { ...snapshot.headerAnchor, left: snapshot.plotBounds.left, width: snapshot.plotBounds.width };
        }
    }
    layout(panes, width, height) {
        const visiblePanes = normalizeVisiblePanes(panes);
        if (visiblePanes.length === 0) {
            return [];
        }
        const hasAnyLeftAxis = visiblePanes.some((pane) => collectVisibleScaleSlots(pane.scaleSlots, "left").length > 0);
        const hasAnyRightAxis = visiblePanes.some((pane) => collectVisibleScaleSlots(pane.scaleSlots, "right").length > 0);
        const globalLeftAxisWidth = hasAnyLeftAxis ? this.axisWidth : 0;
        const globalRightAxisWidth = hasAnyRightAxis ? this.axisWidth : 0;
        const totalRatio = visiblePanes.reduce((sum, pane) => sum + pane.heightRatio, 0);
        let cursorTop = 0;
        // Bresenham-style error accumulation: distribute rounding error across all panes
        // instead of accumulating it all onto the last pane.
        let accumulatedError = 0;
        return visiblePanes.map((pane, index) => {
            const isLastPane = index === visiblePanes.length - 1;
            const remainingHeight = Math.max(1, height - cursorTop);
            const paneHeight = isLastPane
                ? remainingHeight
                : (() => {
                    const exactHeight = (pane.heightRatio / totalRatio) * height;
                    const floored = Math.max(1, Math.floor(exactHeight));
                    accumulatedError += exactHeight - floored;
                    let h = floored;
                    if (accumulatedError >= 1) {
                        const extra = Math.floor(accumulatedError);
                        h += extra;
                        accumulatedError -= extra;
                    }
                    return Math.max(1, Math.min(h, remainingHeight));
                })();
            const leftSlots = collectVisibleScaleSlots(pane.scaleSlots, "left");
            const rightSlots = collectVisibleScaleSlots(pane.scaleSlots, "right");
            const leftAxisWidth = leftSlots.length > 0 ? this.axisWidth : 0;
            const rightAxisWidth = rightSlots.length > 0 ? this.axisWidth : 0;
            const plotWidth = Math.max(1, width - globalLeftAxisWidth - globalRightAxisWidth);
            const top = cursorTop;
            cursorTop += paneHeight;
            const bounds = { top, left: 0, width, height: paneHeight };
            const plotBounds = { top, left: globalLeftAxisWidth, width: plotWidth, height: paneHeight };
            const axisBounds = {};
            const scaleSlotBounds = {};
            const scaleSlotStates = {};
            if (leftAxisWidth > 0) {
                const leftBounds = { top, left: 0, width: leftAxisWidth, height: paneHeight };
                axisBounds.left = leftBounds;
                leftSlots.forEach((slot) => {
                    scaleSlotBounds[slot.id] = cloneBounds(leftBounds);
                    scaleSlotStates[slot.id] = createScaleSlotRuntimeState(slot);
                });
            }
            if (rightAxisWidth > 0) {
                const rightBounds = { top, left: width - rightAxisWidth, width: rightAxisWidth, height: paneHeight };
                axisBounds.right = rightBounds;
                rightSlots.forEach((slot) => {
                    scaleSlotBounds[slot.id] = cloneBounds(rightBounds);
                    scaleSlotStates[slot.id] = createScaleSlotRuntimeState(slot);
                });
            }
            return {
                paneId: pane.id,
                bounds,
                plotBounds,
                axisBounds,
                scaleSlotBounds,
                scaleSlotStates,
                headerAnchor: { top, left: leftAxisWidth, width: plotWidth },
                labelAnchor: { top, left: 0, height: paneHeight },
            };
        });
    }
}
